import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { projectsAPI, invitationsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { Briefcase, Plus, Eye, Edit, Trash2, Clock, CheckCircle, XCircle, AlertCircle, Users, Calendar, Euro, Send, User, RotateCcw, ArrowLeft } from 'lucide-react'

const MyProjectsPage = ({ onNavigate } = {}) => {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [activeTab, setActiveTab] = useState('projects') // 'projects' or 'invitations'
  const [sentInvitations, setSentInvitations] = useState([])
  const [invitationsLoading, setInvitationsLoading] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null) // project being deleted (for confirm dialog)
  const [deletingId, setDeletingId] = useState(null)
  const [deleteError, setDeleteError] = useState('')
  const [undoTarget, setUndoTarget] = useState(null) // project waarvan acceptatie wordt teruggedraaid
  const [undoingId, setUndoingId] = useState(null)
  const [undoError, setUndoError] = useState('')
  const { user } = useAuth()
  const navigate = useNavigate()

  const canDelete = (project) => {
    if (!project) return false
    if (project.payment?.deposit?.status === 'paid') return false
    // Meerdere creators per opdracht: zodra er een creator gekoppeld is (kindproject) niet meer verwijderbaar
    if (project.children?.length) return false
    // Awaiting_deposit / draft / cancelled: altijd verwijderbaar zolang geen geld betaald is
    // (ook bij pakket-boekingen waar selectedCreator al gekoppeld is)
    if (['draft', 'awaiting_deposit', 'cancelled'].includes(project.status)) return true
    // Open: alleen verwijderen als nog geen creator geselecteerd is
    if (project.status === 'open' && !project.selectedCreator) return true
    return false
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    setDeleteError('')
    try {
      await projectsAPI.deleteProject(deleteTarget.id)
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (err) {
      console.error('Delete project error:', err)
      setDeleteError(err?.response?.data?.message || 'Verwijderen mislukt. Probeer opnieuw.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleUndoAccept = async () => {
    if (!undoTarget) return
    setUndoingId(undoTarget.id)
    setUndoError('')
    try {
      await projectsAPI.undoAcceptProposal(undoTarget.id)
      setUndoTarget(null)
      fetchMyProjects()
    } catch (err) {
      console.error('Undo accept error:', err)
      setUndoError(err?.response?.data?.message || 'Terugdraaien mislukt. Probeer opnieuw.')
    } finally {
      setUndoingId(null)
    }
  }

  // Body-scroll-lock zolang een bevestigingsmodal open is (standalone); embedded vangt
  // `overscroll-contain` op de overlay het meescrollen van #dashboard-scroll op.
  useEffect(() => {
    if (!undoTarget && !deleteTarget) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [undoTarget, deleteTarget])

  useEffect(() => {
    if (user?.role !== 'bedrijf' && user?.role !== 'agency') {
      navigate('/dashboard')
      return
    }
    if (activeTab === 'projects') {
      fetchMyProjects()
    } else {
      fetchSentInvitations()
    }

    // Set up polling to refresh every 30 seconds
    const intervalId = setInterval(() => {
      if (activeTab === 'projects') {
        fetchMyProjects()
      } else {
        fetchSentInvitations()
      }
    }, 30000)

    return () => clearInterval(intervalId)
  }, [filter, user, activeTab])

  const fetchMyProjects = async () => {
    try {
      setLoading(true)
      const params = filter !== 'all' ? { status: filter } : {}
      const response = await projectsAPI.getMyProjects(params)
      setProjects(response.data.projects || [])
    } catch (error) {
      console.error('Error fetching projects:', error)
      setError('Er is een fout opgetreden bij het ophalen van je opdrachten')
    } finally {
      setLoading(false)
    }
  }

  const fetchSentInvitations = async () => {
    try {
      setInvitationsLoading(true)
      const response = await invitationsAPI.getSent()
      setSentInvitations(response.data.invitations || [])
    } catch (error) {
      console.error('Error fetching sent invitations:', error)
      setError('Er is een fout opgetreden bij het ophalen van je uitnodigingen')
    } finally {
      setInvitationsLoading(false)
    }
  }

  const getInvitationStatusColor = (status) => {
    const colors = {
      pending: 'text-[#D97706] border-[#F59E0B]/40',
      accepted: 'text-[#3EB489] border-[#3EB489]/40',
      declined: 'text-[#EF4444] border-red-200',
      cancelled: 'text-[#94A3B8] border-gray-200'
    }
    return colors[status] || 'text-[#94A3B8] border-gray-200'
  }

  const getInvitationStatusText = (status) => {
    const texts = {
      pending: 'Wacht op reactie',
      accepted: 'Geaccepteerd',
      declined: 'Afgewezen',
      cancelled: 'Geannuleerd'
    }
    return texts[status] || status
  }

  // Status als rustige pill: gekleurde rand + tekst, geen ingekleurd vlak
  const getStatusColor = (status) => {
    const colors = {
      draft: 'text-[#94A3B8] border-gray-200',
      open: 'text-[#1D8BF8] border-[#1D8BF8]/30',
      awaiting_deposit: 'text-[#D97706] border-[#F59E0B]/40',
      proposal_accepted: 'text-[#D97706] border-[#F59E0B]/40',
      awaiting_product_delivery: 'text-[#D97706] border-[#F59E0B]/40',
      creator_selected: 'text-[#D97706] border-[#F59E0B]/40',
      in_progress: 'text-[#D97706] border-[#F59E0B]/40',
      delivered: 'text-[#7c3aed] border-[#7c3aed]/30',
      under_review: 'text-[#7c3aed] border-[#7c3aed]/30',
      in_review: 'text-[#7c3aed] border-[#7c3aed]/30',
      revision_requested: 'text-[#D97706] border-[#F59E0B]/40',
      approved: 'text-[#3EB489] border-[#3EB489]/40',
      completed: 'text-[#3EB489] border-[#3EB489]/40',
      cancelled: 'text-[#EF4444] border-red-200',
      refunded: 'text-[#94A3B8] border-gray-200',
      disputed: 'text-[#EF4444] border-red-200'
    }
    return colors[status] || 'text-[#94A3B8] border-gray-200'
  }

  const getStatusText = (status) => {
    const texts = {
      draft: 'Concept',
      open: 'Open voor voorstellen',
      awaiting_deposit: 'Wacht op aanbetaling',
      proposal_accepted: 'Wacht op aanbetaling',
      awaiting_product_delivery: 'Product onderweg',
      creator_selected: 'Creator gekozen',
      in_progress: 'In uitvoering',
      delivered: 'Opgeleverd',
      under_review: 'In beoordeling',
      in_review: 'In beoordeling',
      revision_requested: 'Revisie gevraagd',
      approved: 'Goedgekeurd',
      completed: 'Voltooid',
      cancelled: 'Geannuleerd',
      refunded: 'Terugbetaald',
      disputed: 'In geschil'
    }
    return texts[status] || status
  }

  const getStatusIcon = (status) => {
    switch(status) {
      case 'draft': return <Edit className="w-4 h-4" />
      case 'open': return <Clock className="w-4 h-4" />
      case 'in_progress': return <Users className="w-4 h-4" />
      case 'completed': return <CheckCircle className="w-4 h-4" />
      case 'cancelled': return <XCircle className="w-4 h-4" />
      default: return <AlertCircle className="w-4 h-4" />
    }
  }

  const formatShortDate = (date) => {
    return new Date(date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const formatEuro = (amount) => new Intl.NumberFormat('nl-NL', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(amount)

  const getDaysRemaining = (deadline) => {
    const now = new Date()
    const deadlineDate = new Date(deadline)
    const diffTime = deadlineDate - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return <span className="text-[#EF4444] font-medium">Verlopen</span>
    if (diffDays === 0) return <span className="text-[#D97706] font-medium">Vandaag</span>
    if (diffDays === 1) return <span className="text-[#D97706] font-medium">Morgen</span>
    if (diffDays < 7) return <span className="text-[#D97706] font-medium">Nog {diffDays} dagen</span>
    return <span className="text-[#0F172A] font-medium">Nog {diffDays} dagen</span>
  }

  if (loading && projects.length === 0) {
    return (
      <div className="font-['Inter'] min-h-screen flex items-center justify-center bg-white text-[#0F172A]">
        <div className="w-8 h-8 border-2 border-[#1D8BF8] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="font-['Inter'] min-h-screen flex flex-col bg-[#F8FAFC] text-[#0F172A]">
      {/* Header — alleen op de losse route /my-projects. Ingesloten in het dashboard
          (onNavigate aanwezig) heeft de shell al een eigen header en sidebar; deze
          erbij rendert een tweede navigatiebalk bovenop de eerste. */}
      {!onNavigate && (
        <header className="w-full flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 sticky top-0 z-50 bg-white/95 backdrop-blur-xl">
          <div className="flex items-center gap-3 sm:gap-6">
            <Link to="/dashboard" className="md:hidden p-2.5 -ml-2 rounded-lg hover:bg-gray-100 transition" aria-label="Terug naar dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Link to="/" className="text-2xl font-black tracking-tighter text-[#0F172A]">
              UGC<span className="text-[#1D8BF8]">4you</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-sm hover:text-[#1D8BF8] transition">Dashboard</Link>
              <Link to="/my-projects" className="text-sm text-[#1D8BF8]">Mijn Opdrachten</Link>
              <Link to="/creators" className="text-sm hover:text-[#1D8BF8] transition">Creators</Link>
            </nav>
          </div>

          <button
            onClick={() => navigate('/projects/create')}
            className="px-4 py-2 bg-[#3EB489] text-white rounded-lg font-medium hover:bg-[#359E78] transition flex items-center gap-2 shadow-md whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nieuwe Opdracht</span>
            <span className="sm:hidden">Nieuw</span>
          </button>
        </header>
      )}

      {/* Main Content */}
      <div className="flex-1 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Page Header */}
          <div className="mb-8 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight">Mijn Opdrachten</h1>
              <p className="text-[#475569] mt-3">Beheer al je UGC opdrachten op één plek</p>
            </div>
            <button
              onClick={() => onNavigate ? onNavigate('create-project') : navigate('/projects/create')}
              className="px-5 py-3 bg-[#3EB489] text-white rounded-xl font-semibold hover:bg-[#359E78] transition flex items-center gap-2 shadow-sm shadow-[#3EB489]/20 flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nieuwe Opdracht</span>
              <span className="sm:hidden">Nieuw</span>
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-white border border-red-200 rounded-xl">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-[#EF4444] mr-3 flex-shrink-0" />
                <p className="text-sm text-[#EF4444]">{error}</p>
              </div>
            </div>
          )}

          {/* Main Tabs */}
          <div className="mb-7 flex gap-5 sm:gap-7 border-b border-gray-200">
            {[
              { key: 'projects', label: 'Opdrachten', shortLabel: 'Opdrachten', Icon: Briefcase },
              { key: 'invitations', label: 'Verstuurde uitnodigingen', shortLabel: 'Uitnodigingen', Icon: Send }
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`-mb-px pb-3 border-b-2 font-semibold text-sm transition flex items-center gap-2 whitespace-nowrap ${
                  activeTab === t.key
                    ? 'border-[#1D8BF8] text-[#1D8BF8]'
                    : 'border-transparent text-[#475569] hover:text-[#0F172A]'
                }`}
              >
                <t.Icon className="w-4 h-4" />
                <span className="sm:hidden">{t.shortLabel}</span>
                <span className="hidden sm:inline">{t.label}</span>
                {t.key === 'invitations' && sentInvitations.length > 0 && activeTab !== 'invitations' && (
                  <span className="px-1.5 py-0.5 rounded-md border border-gray-200 text-[11px] font-medium text-[#475569]">
                    {sentInvitations.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {activeTab === 'projects' && (
            <>
          {/* Filter Tabs */}
          <div className="mb-7 flex flex-wrap gap-2">
            {['all', 'draft', 'open', 'in_progress', 'completed', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3.5 py-2 sm:py-1.5 rounded-full text-sm font-medium transition border ${
                  filter === status
                    ? 'bg-[#1D8BF8] text-white border-[#1D8BF8]'
                    : 'bg-white text-[#475569] border-gray-200 hover:border-[#1D8BF8]'
                }`}
              >
                {status === 'all' ? 'Alle' : getStatusText(status)}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          {projects.length === 0 ? (
            <div className="text-center py-16 px-6 rounded-2xl bg-white border border-gray-200">
              <Briefcase className="w-14 h-14 text-gray-200 mx-auto mb-5" />
              <h3 className="text-xl font-bold">Geen opdrachten gevonden</h3>
              <p className="text-[#475569] mt-3 mb-7">
                {filter === 'all'
                  ? "Je hebt nog geen opdrachten aangemaakt"
                  : `Je hebt geen opdrachten met status "${getStatusText(filter)}"`
                }
              </p>
              {filter === 'all' && (
                <button
                  onClick={() => onNavigate ? onNavigate('create-project') : navigate('/projects/create')}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition shadow-sm shadow-[#3EB489]/20"
                >
                  <Plus className="w-4 h-4" />
                  Maak je eerste opdracht
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
              {projects.map((project) => {
                // Binnen het dashboard via onNavigate openen, niet via een <Link>. Een harde
                // route-navigatie mount de losstaande pagina zonder onNavigate, waardoor je de
                // sidebar kwijtraakt en op de publieke projectweergave belandt.
                const openProject = () => {
                  const view = project.selectedCreator ? 'project-workspace' : 'project-detail'
                  const pad = project.selectedCreator
                    ? `/project-workspace/${project.id}`
                    : `/projects/${project.id}`
                  onNavigate ? onNavigate(view, project.id) : navigate(pad)
                }
                const openProposals = () => onNavigate
                  ? onNavigate('project-proposals', project.id)
                  : navigate(`/projects/${project.id}/proposals`)

                // Meerdere creators per opdracht: elke gekozen creator heeft een eigen
                // kindproject (children) met eigen aanbetaling en workspace. De opdracht
                // zelf blijft open voor meer voorstellen.
                const children = project.children || []
                const hasChildren = children.length > 0
                const isPaymentDue = !hasChildren && (project.status === 'awaiting_deposit' || project.status === 'proposal_accepted')
                // Voorstellen zijn ook bij een lopende opdracht (creator al gekozen) nog te bekijken en te accepteren
                const hasProposals = project.status !== 'draft' && project.stats?.proposalsCount > 0
                const showProposalsSecondary = hasProposals && project.selectedCreator && !hasChildren
                const canEdit = project.status === 'draft' || project.status === 'open'
                const secondaryBtn = 'px-3 py-2.5 rounded-xl border border-gray-200 text-[#475569] hover:border-[#1D8BF8] hover:text-[#1D8BF8] transition inline-flex items-center justify-center gap-1.5 text-xs font-medium'

                return (
                <div key={project.id} className="group relative h-full">
                  <div className={`relative h-full flex flex-col rounded-2xl bg-white border transition-all duration-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_16px_40px_-20px_rgba(15,23,42,0.25)] ${
                    project.status === 'cancelled' ? 'border-gray-200 opacity-70' : 'border-gray-200 hover:border-[#1D8BF8]/40'
                  }`}>

                    {/* ===== Status ===== */}
                    <div className="p-5 md:p-6">
                      <div className="flex justify-between items-start gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${getStatusColor(project.status)}`}>
                          {getStatusIcon(project.status)}
                          {getStatusText(project.status)}
                        </span>
                        {project.stats?.proposalsCount > 0 && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 text-[11px] font-medium text-[#475569] whitespace-nowrap">
                            <Users className="w-3 h-3 text-[#94A3B8]" />
                            {project.stats.proposalsCount} voorstel{project.stats.proposalsCount === 1 ? '' : 'len'}
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-bold leading-snug line-clamp-2 break-words mt-4">{project.title}</h3>
                      <p className="text-[#475569] text-sm leading-relaxed line-clamp-3 mt-3">{project.description}</p>
                    </div>

                    {/* ===== Kerngegevens ===== */}
                    <div className="px-5 md:px-6 py-4 border-y border-gray-100">
                      <div className="flex items-end justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">Budget</p>
                          <p className="text-xl font-black tracking-tight tabular-nums text-[#0F172A] mt-2">
                            {formatEuro((project.budget || 0) * 1.21)}
                          </p>
                          <p className="text-[11px] text-[#94A3B8] mt-1.5">incl. BTW</p>
                        </div>
                        {project.deadline && (
                          <div className="text-right flex-shrink-0">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">Deadline</p>
                            <p className="text-sm mt-2">{getDaysRemaining(project.deadline)}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ===== Acties ===== */}
                    <div className="mt-auto p-5 md:p-6">
                      {project.publishedAt && (
                        <div className="flex items-center gap-1.5 text-[11px] text-[#94A3B8] mb-4">
                          <Clock className="w-3 h-3" />
                          Geplaatst op {formatShortDate(project.publishedAt)}
                        </div>
                      )}

                      {/* Gekozen creators — elk met eigen aanbetaling en workspace */}
                      {hasChildren && (
                        <div className="mb-5">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">
                            Creators ({children.length})
                          </p>
                          <ul className="divide-y divide-gray-100 border-y border-gray-100">
                            {children.map((child) => {
                              const depositPaid = child.payment?.deposit?.status === 'paid'
                              const canUndoChild = !depositPaid && child.status === 'proposal_accepted'
                              const naam = child.selectedCreator?.name || child.selectedCreator?.firstName || 'Creator'
                              const initial = naam.charAt(0).toUpperCase()
                              const openChildWorkspace = () => onNavigate
                                ? onNavigate('project-workspace', child.id)
                                : navigate(`/project-workspace/${child.id}`)
                              return (
                                <li key={child.id} className="py-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0 ring-1 ring-gray-200" style={{ background: child.selectedCreator?.profilePictureUrl ? '#F1F5F9' : '#3EB489', display: 'grid', placeItems: 'center' }}>
                                      {child.selectedCreator?.profilePictureUrl ? (
                                        <img src={child.selectedCreator.profilePictureUrl} alt={naam} className="w-full h-full object-cover" />
                                      ) : (
                                        <span className="text-white text-sm font-bold">{initial}</span>
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-semibold text-[#0F172A] truncate">{naam}</p>
                                      <p className={`text-[11px] mt-0.5 ${getStatusColor(child.status).split(' ')[0]}`}>
                                        {getStatusText(child.status)}
                                        {child.budget ? <span className="text-[#94A3B8]"> · {formatEuro(child.budget)} excl. BTW</span> : null}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2 mt-2.5">
                                    {depositPaid ? (
                                      <button
                                        onClick={openChildWorkspace}
                                        className="flex-1 px-3 py-2 rounded-xl bg-[#3EB489] hover:bg-[#359E78] text-white text-xs font-semibold transition inline-flex items-center justify-center gap-1.5"
                                      >
                                        <Briefcase className="w-3.5 h-3.5" />
                                        Open workspace
                                      </button>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => navigate(`/projects/${child.id}/payment`)}
                                          className="flex-1 px-3 py-2 rounded-xl bg-[#F59E0B] hover:bg-[#D97706] text-white text-xs font-semibold transition inline-flex items-center justify-center gap-1.5"
                                        >
                                          <Euro className="w-3.5 h-3.5" />
                                          Betaal aanbetaling
                                        </button>
                                        {canUndoChild && (
                                          <button
                                            onClick={() => { setUndoError(''); setUndoTarget({ id: child.id, title: project.title, creatorName: naam }) }}
                                            className={`${secondaryBtn} min-w-[7rem] whitespace-nowrap`}
                                            title="Reservering terugdraaien"
                                          >
                                            <RotateCcw className="w-3.5 h-3.5" />
                                            Terugdraaien
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Eén duidelijke hoofdactie per kaart: betalen > workspace > voorstellen > bekijken */}
                      {isPaymentDue ? (
                        <button
                          onClick={() => navigate(`/projects/${project.id}/payment`)}
                          className="w-full px-5 py-3.5 rounded-xl bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold text-sm transition inline-flex items-center justify-center gap-2 shadow-sm shadow-[#F59E0B]/20"
                        >
                          <Euro className="w-4 h-4" />
                          Betaal aanbetaling (10%)
                        </button>
                      ) : project.selectedCreator && !hasChildren ? (
                        <button
                          onClick={openProject}
                          className="w-full px-5 py-3.5 rounded-xl bg-[#3EB489] hover:bg-[#359E78] text-white font-semibold text-sm transition inline-flex items-center justify-center gap-2 shadow-sm shadow-[#3EB489]/20"
                        >
                          <Briefcase className="w-4 h-4" />
                          Open workspace
                        </button>
                      ) : hasProposals || hasChildren ? (
                        <button
                          onClick={openProposals}
                          className="w-full px-5 py-3.5 rounded-xl bg-[#3EB489] hover:bg-[#359E78] text-white font-semibold text-sm transition inline-flex items-center justify-center gap-2 shadow-sm shadow-[#3EB489]/20"
                        >
                          <Users className="w-4 h-4" />
                          Bekijk {project.stats?.proposalsCount || 0} voorstel{project.stats?.proposalsCount === 1 ? '' : 'len'}
                        </button>
                      ) : (
                        <button
                          onClick={openProject}
                          className="w-full px-5 py-3.5 rounded-xl border border-gray-200 text-[#0F172A] hover:border-[#1D8BF8] hover:text-[#1D8BF8] font-semibold text-sm transition inline-flex items-center justify-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          Opdracht bekijken
                        </button>
                      )}

                      {/* Nevenacties */}
                      {(isPaymentDue || project.selectedCreator || hasProposals || canEdit || canDelete(project)) && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(isPaymentDue || project.selectedCreator || hasProposals) && (
                            <button onClick={openProject} className={`${secondaryBtn} flex-1 min-w-[6rem]`}>
                              <Eye className="w-3.5 h-3.5" />
                              Bekijken
                            </button>
                          )}
                          {showProposalsSecondary && (
                            <button onClick={openProposals} className={`${secondaryBtn} flex-1 min-w-[7rem]`} title="Voorstellen bekijken of extra creators kiezen">
                              <Users className="w-3.5 h-3.5" />
                              Voorstellen ({project.stats?.proposalsCount || 0})
                            </button>
                          )}
                          {project.status === 'proposal_accepted' && project.payment?.deposit?.status !== 'paid' && (
                            <button
                              onClick={() => { setUndoError(''); setUndoTarget(project) }}
                              className={`${secondaryBtn} flex-1 min-w-[7rem]`}
                              title="Acceptatie terugdraaien"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                              Terugdraaien
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => onNavigate ? onNavigate('project-detail', project.id) : navigate(`/projects/${project.id}/edit`)}
                              className={`${secondaryBtn} flex-1 min-w-[6rem]`}
                              aria-label="Bewerken"
                            >
                              <Edit className="w-3.5 h-3.5" />
                              Bewerken
                            </button>
                          )}
                          {canDelete(project) && (
                            <button
                              onClick={() => { setDeleteError(''); setDeleteTarget(project) }}
                              disabled={deletingId === project.id}
                              className="px-3 py-2.5 rounded-xl border border-gray-200 text-[#94A3B8] hover:border-red-300 hover:text-[#EF4444] transition inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                              aria-label="Verwijderen"
                              title="Verwijder opdracht"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}

                      {/* Toelichting hoort niet in de knoppenrij: als flex-item concurreerde
                          deze tekst met de knoppen om de breedte, waardoor labels als
                          "Voorstellen (5)" over twee regels braken. */}
                      {!project.selectedCreator && !hasChildren && project.status !== 'awaiting_deposit' && (
                        <p className="mt-4 text-[11px] text-[#94A3B8] text-center">
                          Workspace beschikbaar na creator selectie
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                )
              })}
            </div>
          )}
            </>
          )}

          {/* Sent Invitations Tab */}
          {activeTab === 'invitations' && (
            <>
              {invitationsLoading ? (
                <div className="text-center py-16">
                  <div className="w-8 h-8 border-2 border-[#1D8BF8] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-[#475569]">Uitnodigingen laden...</p>
                </div>
              ) : sentInvitations.length === 0 ? (
                <div className="text-center py-16 px-6 rounded-2xl bg-white border border-gray-200">
                  <Send className="w-14 h-14 text-gray-200 mx-auto mb-5" />
                  <h3 className="text-xl font-bold">Nog geen uitnodigingen verstuurd</h3>
                  <p className="text-[#475569] mt-3 mb-7">
                    Ga naar een creator profiel om ze uit te nodigen voor een opdracht.
                  </p>
                  <Link
                    to="/creators"
                    className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition shadow-sm shadow-[#3EB489]/20"
                  >
                    <Users className="w-4 h-4" />
                    Bekijk creators
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {sentInvitations.map((invitation) => {
                    const creator = invitation.creator
                    const project = invitation.project
                    const creatorName = creator?.firstName && creator?.lastName
                      ? `${creator.firstName} ${creator.lastName}`
                      : creator?.displayName || 'Onbekende creator'
                    const creatorId = creator?._id || creator?.id

                    return (
                      <div key={invitation._id || invitation.id} className="p-5 md:p-6 rounded-2xl bg-white border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-[#1D8BF8]/40 hover:shadow-[0_16px_40px_-20px_rgba(15,23,42,0.25)] transition-all duration-200">
                        <div className="flex items-start gap-4">
                          {/* Creator Avatar */}
                          <button onClick={() => creatorId && (onNavigate ? onNavigate('creator-detail', creatorId) : navigate(`/creators/${creatorId}`))} className="flex-shrink-0">
                            {creator?.profilePictureUrl ? (
                              <img
                                src={creator.profilePictureUrl}
                                alt={creatorName}
                                className="w-12 h-12 rounded-xl object-cover ring-1 ring-gray-200"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-[#3EB489] flex items-center justify-center ring-1 ring-gray-200">
                                <User className="w-6 h-6 text-white" />
                              </div>
                            )}
                          </button>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <button onClick={() => creatorId && (onNavigate ? onNavigate('creator-detail', creatorId) : navigate(`/creators/${creatorId}`))} className="font-bold text-[#0F172A] hover:text-[#1D8BF8] transition">
                                  {creatorName}
                                </button>
                                {project && (
                                  <p className="text-sm text-[#475569] mt-1.5">
                                    Uitgenodigd voor: <button onClick={() => onNavigate ? onNavigate('project-detail', project._id || project.id) : navigate(`/projects/${project._id || project.id}`)} className="text-[#1D8BF8] hover:underline">{project.title}</button>
                                  </p>
                                )}
                              </div>
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold flex-shrink-0 ${getInvitationStatusColor(invitation.status)}`}>
                                {invitation.status === 'pending' && <Clock className="w-3 h-3" />}
                                {invitation.status === 'accepted' && <CheckCircle className="w-3 h-3" />}
                                {invitation.status === 'declined' && <XCircle className="w-3 h-3" />}
                                {getInvitationStatusText(invitation.status)}
                              </span>
                            </div>

                            {/* Details row */}
                            <div className="flex flex-wrap items-center gap-2 mt-4">
                              {project?.budget && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                                  <Euro className="w-3 h-3 text-[#94A3B8]" />
                                  <strong className="font-semibold text-[#0F172A]">{formatEuro(project.budget * 1.21)}</strong> incl. BTW
                                </span>
                              )}
                              {project?.deadline && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                                  <Calendar className="w-3 h-3 text-[#94A3B8]" />
                                  {formatShortDate(project.deadline)}
                                </span>
                              )}
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                                <Clock className="w-3 h-3 text-[#94A3B8]" />
                                Verstuurd {formatShortDate(invitation.createdAt)}
                              </span>
                            </div>

                            {/* Message preview */}
                            {invitation.message && (
                              <p className="mt-4 text-sm text-[#475569] leading-relaxed line-clamp-2 pl-3.5 border-l-2 border-[#1D8BF8]/25">
                                {invitation.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Undo-accept confirmation modal */}
      {undoTarget && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto overscroll-contain"
          onClick={() => { if (!undoingId) setUndoTarget(null) }}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 text-[#475569] flex items-center justify-center">
                <XCircle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[#0F172A]">Acceptatie terugdraaien?</h3>
                <p className="text-sm text-[#475569] mt-1">
                  {undoTarget.creatorName ? (
                    <>
                      Je reservering van <span className="font-semibold text-[#0F172A]">{undoTarget.creatorName}</span> voor <span className="font-semibold text-[#0F172A]">"{undoTarget.title}"</span> wordt teruggedraaid.
                      Het voorstel komt weer open te staan; je andere creators en je opdracht blijven ongewijzigd.
                      Er is niets betaald, dus dit kost je niets.
                    </>
                  ) : (
                    <>
                      Je acceptatie voor <span className="font-semibold text-[#0F172A]">"{undoTarget.title}"</span> wordt teruggedraaid.
                      Het voorstel van de creator komt weer beschikbaar, je opdracht staat weer open en je kunt opnieuw kiezen.
                      Er is niets betaald, dus dit kost je niets.
                    </>
                  )}
                </p>
              </div>
            </div>

            {undoError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {undoError}
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={() => setUndoTarget(null)}
                disabled={!!undoingId}
                className="px-4 py-2 bg-[#F8FAFC] hover:bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium transition disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                onClick={handleUndoAccept}
                disabled={!!undoingId}
                className="px-4 py-2 bg-[#0F172A] hover:bg-[#1e293b] text-white rounded-lg text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {undoingId ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Terugdraaien...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Ja, draai terug
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto overscroll-contain"
          onClick={() => { if (!deletingId) setDeleteTarget(null) }}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[#0F172A]">Opdracht verwijderen?</h3>
                <p className="text-sm text-[#475569] mt-1">
                  Weet je zeker dat je <span className="font-semibold text-[#0F172A]">"{deleteTarget.title}"</span> wilt verwijderen?
                  Deze actie kan niet ongedaan worden gemaakt. Eventuele voorstellen en uitnodigingen voor deze opdracht worden ook verwijderd.
                </p>
              </div>
            </div>

            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {deleteError}
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={!!deletingId}
                className="px-4 py-2 bg-[#F8FAFC] hover:bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium transition disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                onClick={handleDelete}
                disabled={!!deletingId}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deletingId ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verwijderen...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Verwijderen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MyProjectsPage